import React, { useRef, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { db } from "../firebase";
import { collection, addDoc, Timestamp, getDocs } from "firebase/firestore";
import { calculateTenure, calculateInterest, calculateOrnamentAmount, calculateTotalPayable, formatCurrencyINR, parseGramInput, calculateTotalOrnamentNetGrams } from "../utils/calculations";
import { MAX_ORNAMENT_FILE_BYTES, compressImageFile, compressDataUrl } from "../utils/imageUtils";
import { generateBillingInvoicePdf } from "../utils/pdfGenerator";
import { validateBillingForm, validateCustomerForm } from "../utils/validations";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loaderSlice";

// ---------- Layout ----------
const Container = styled.div`
  margin-top: 60px;
  background: ${({ theme }) => theme.colors.background};
  min-height: 100vh;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 40px;
    overflow-x: hidden;
    overflow-x: hidden;
  }
`;


const StepperContainer = styled.div`
  margin-bottom: 24px;
`;

const StepperHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StepText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: #2563eb;
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease-in-out;
`;

const StepWrapper = styled.div`
  display: ${({ isActive }) => (isActive ? "block" : "none")};
  @media print {
    display: block !important;
  }
`;

const StepNavigation = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;

  @media print {
    display: none;
  }
`;

const CustomUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #e5e7eb;
  }

  input {
    display: none;
  }
`;

// Customer grid moved to CreateFormGrid for inline customer details

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.padding};
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
`;

const HeaderRight = styled.div`
  text-align: right;
  @media (max-width: 768px) {
    text-align: left;
    width: 100%;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const SectionTitle = styled.h3`
  margin-top: 20px;
  margin-bottom: 16px;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    button {
      flex: 1;
    }
  }
`;

const SummaryBox = styled.div`
  text-align: right;
  min-width: 260px;
  flex-grow: 1;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;

  @media (max-width: 768px) {
    min-width: 100%;
    text-align: left;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  align-items: center;
  
  span {
    color: #6b7280;
  }
  strong {
    color: #111827;
  }
`;

const SummaryTotalRow = styled(SummaryRow)`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  font-size: 16px;
  
  span {
    color: #4b5563;
    font-weight: bold;
  }
`;

const Step3SummaryBand = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const OrnamentTotalsBox = styled(SummaryBox)`
  text-align: left;
  flex: 1 1 240px;
  min-width: 0;
  margin-top: 0;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    width: 100%;
  }
`;

const LoanSummaryBoxSide = styled(SummaryBox)`
  text-align: right;
  flex: 1 1 280px;
  min-width: 0;
  margin-top: 0;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    width: 100%;
    text-align: left;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 15px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 12px;
  margin-bottom: 4px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ hasError, theme }) =>
    hasError ? "#DC2626" : theme.colors.border};
  border-radius: 6px;
  padding: 0 8px;
  background: ${({ readOnly }) => readOnly ? "#f3f4f6" : "#fff"};
  width: 100%;
  min-width: 0; /* 🔥 critical fix */

  &:focus-within {
    border-color: ${({ hasError, readOnly, theme }) =>
    readOnly ? theme.colors.border : hasError ? "#DC2626" : theme.colors.accent};
  }
`;

const Prefix = styled.span`margin-right:6px; font-size: 12px;`;
const Suffix = styled.span`margin-left:6px; font-size: 12px;`;

const Input = styled.input`
  border: none;
  outline: none;
  padding: 10px;
  width: 100%;
  min-width: 0; /* 🔥 prevents overflow */
  background: transparent;
`;
const Error = styled.span`
  color: #DC2626;
  font-size: 11px;
  margin-top: 4px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    border: 1px solid ${({ theme }) => theme.colors.border};
    padding: 8px;
    text-align: center;
    white-space: nowrap;
  }
`;

const MobileContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const MobileCard = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border: 1px solid ${({ theme }) => theme.colors.border || "#e5e7eb"};
  position: relative;
`;

const MobileRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const MobileLabel = styled.span`
  color: #6b7280;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const DeleteBtnMobile = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #FEE2E2;
  color: #991B1B;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
const IconButton = styled.button`
  background: transparent;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  margin-top: 15px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OutlinedButton = styled.button`
  margin-top: 15px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.accent};
  // color: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrintArea = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  @media print {
    body * { visibility: hidden; }
    &, & * { visibility: visible; }
    position: absolute;
    top: 0;
    width: 100%;
    background: #fff;
    overflow-x: visible;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
    width: 95%;
  }
`;

const CustomerCardItem = styled.div`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:hover {
    background: #f3f4f6;
    border-color: #dbeafe;
  }
`;

const CloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 20px;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

// Create modal removed — create customer fields are inline in Customer Details

const CreateFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CreateFormRow = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 13px;
    margin-bottom: 6px;
    font-weight: 600;
    color: #374151;
  }

  input {
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid ${({ hasError }) => (hasError ? "#DC2626" : "#D1D5DB")};
    font-size: 14px;
    background: #ffffff;
    color: #111827;
  }
`;

const CreateErrorText = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 6px;
`;

// CreateButtonRow removed; controls moved inline

const MAX_FILE_BYTES = 819200; // 800 KB

/* ---------- Utils ---------- */

const onlyNumbers = (v) => v.replace(/[^0-9]/g, "");

const onlyText = (v) => v.replace(/[^a-zA-Z ]/g, "");

const createEmptyCustomerForm = () => ({
  customerId: "",
  customerName: "",
  phone: "",
  email: "",
  address: "",
  profilePicture: "",
  profilePictureName: "",
  aadharCard: "",
  aadharCardName: "",
  panCard: "",
  panCardName: "",
  bankName: "",
  branch: "",
  accountNumber: "",
  ifsc: "",
});

/* convert file → base64 (for Firebase storage) */
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

/* ---------- Component ---------- */
const Billing = () => {
  const printRef = useRef();
  const [customerId, setCustomerId] = useState("");

  useEffect(() => {
    const fetchBillingId = async () => {
      try {
        const snap = await getDocs(collection(db, "billing"));
        let maxId = 0;
        snap.docs.forEach(doc => {
          const data = doc.data();
          if (data.customerId && data.customerId.startsWith("GL-202026")) {
            const num = parseInt(data.customerId.replace("GL-202026", ""), 10);
            if (!isNaN(num) && num > maxId) {
              maxId = num;
            }
          }
        });
        setCustomerId(`GL-202026${String(maxId + 1).padStart(3, "0")}`);
      } catch (err) {
        console.error("Failed to generate billing ID", err);
      }
    };
    fetchBillingId();

    const fetchNextCustomerId = async () => {
      dispatch(setLoading(true));
      try {
        const snap = await getDocs(collection(db, "customers"));
        let maxId = 0;
        snap.docs.forEach(doc => {
          const data = doc.data();
          if (data.customerId && data.customerId.startsWith("MAPS")) {
            const num = parseInt(data.customerId.replace("MAPS", ""), 10);
            if (!isNaN(num) && num > maxId) {
              maxId = num;
            }
          }
        });
        const nextId = `MAPS${String(maxId + 1).padStart(5, "0")}`;
        setForm(prev => ({ ...prev, customerId: nextId }));
      } catch (err) {
        console.error("Failed to generate customer ID", err);
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchNextCustomerId();
  }, []);
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    bankName: "",
    accountNumber: "",
    ifsc: "",
    customerName: "",
    customerId: "",
    phone: "",
    totalWeight: "",
    loanAmount: "",
    interest: "",
    processingFeePercent: "",
    startDate: "",
    endDate: "",
    tenure: "",
    goldRate: "",
    includeGst: false,
    gstPercent: "",
  });

  const [customersList, setCustomersList] = useState([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
  const [customerFormData, setCustomerFormData] = useState(createEmptyCustomerForm());
  const [customerFormErrors, setCustomerFormErrors] = useState({});
  const [customerTouchedFields, setCustomerTouchedFields] = useState({});
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [touchedOrnaments, setTouchedOrnaments] = useState({});
  const [isSaved, setIsSaved] = useState(false);

  const fetchCustomers = async () => {
    setIsFetchingCustomers(true);
    try {
      const snap = await getDocs(collection(db, "customers"));
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomersList(data);
      setShowCustomerModal(true);
      toast.dismiss();
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to load customers");
    } finally {
      setIsFetchingCustomers(false);
    }
  };

  const handleSelectCustomer = (cust) => {
    // Populate all relevant form fields from selected customer, falling back to empty values
    setForm((prev) => ({
      ...prev,
      customerId: cust.customerId || prev.customerId || "",
      customerName: cust.customerName || prev.customerName || "",
      phone: cust.phone || prev.phone || "",
      email: cust.email || prev.email || "",
      address: cust.address || prev.address || "",
      bankName: cust.bankName || prev.bankName || "",
      branch: cust.branch || prev.branch || "",
      accountNumber: cust.accountNumber || prev.accountNumber || "",
      ifsc: cust.ifsc || prev.ifsc || "",
      profilePicture: cust.profilePicture || prev.profilePicture || "",
      profilePictureName: cust.profilePictureName || prev.profilePictureName || "",
      aadharCard: cust.aadharCard || prev.aadharCard || "",
      aadharCardName: cust.aadharCardName || prev.aadharCardName || "",
      panCard: cust.panCard || prev.panCard || "",
      panCardName: cust.panCardName || prev.panCardName || "",
    }));
    setShowCustomerModal(false);
    setIsCustomerSelected(true);
    toast.success("Customer selected");
  };

  useEffect(() => {
    const payload = {
      customerId: form.customerId || "",
      customerName: form.customerName || "",
      phone: form.phone || "",
      email: form.email || "",
      address: form.address || "",
      bankName: form.bankName || "",
      branch: form.branch || "",
      accountNumber: form.accountNumber || "",
      ifsc: form.ifsc || "",
      profilePicture: form.profilePicture || "",
      profilePictureName: form.profilePictureName || "",
      aadharCard: form.aadharCard || "",
      aadharCardName: form.aadharCardName || "",
      panCard: form.panCard || "",
      panCardName: form.panCardName || "",
    };
    const { errors: nextErrors } = validateCustomerForm(payload);
    const visibleErrors = Object.entries(nextErrors).reduce((acc, [key, value]) => {
      if (touchedFields[key] || customerTouchedFields[key]) acc[key] = value;
      return acc;
    }, {});
    setCustomerFormErrors(visibleErrors);
  }, [form, touchedFields, customerTouchedFields]);

  const validateCustomerField = (key) => {
    setCustomerTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  const handleCustomerFileUpload = async (event, fieldName, nameField) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      toast.error("File too large. Max 800 KB allowed.");
      try { event.target.value = ""; } catch (e) { }
      return;
    }

    try {
      const encoded = await toBase64(file);
      setCustomerFormData((prev) => ({
        ...prev,
        [fieldName]: encoded,
        [nameField]: file.name,
      }));
      // also mirror into main form so inline customer details reflect uploaded files
      setForm((prev) => ({
        ...prev,
        [fieldName]: encoded,
        [nameField]: file.name,
      }));
      // editing fields via upload means this is a new/edited customer
      setIsCustomerSelected(false);
      validateCustomerField(fieldName);
    } catch (error) {
      toast.error("Failed to read file");
    }
  };

  const handleAddCustomer = async () => {
    // Build payload from form (customer fields)
    const payload = {
      customerId: form.customerId || "",
      customerName: form.customerName || "",
      phone: form.phone || "",
      email: form.email || "",
      address: form.address || "",
      bankName: form.bankName || "",
      branch: form.branch || "",
      accountNumber: form.accountNumber || "",
      ifsc: form.ifsc || "",
      profilePicture: form.profilePicture || "",
      profilePictureName: form.profilePictureName || "",
      aadharCard: form.aadharCard || "",
      aadharCardName: form.aadharCardName || "",
      panCard: form.panCard || "",
      panCardName: form.panCardName || "",
    };

    const validation = validateCustomerForm(payload);
    if (!validation.isValid) {
      // show errors inline using customerTouchedFields / customerFormErrors
      const markAllTouched = Object.keys(validation.errors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setCustomerTouchedFields((prev) => ({ ...prev, ...markAllTouched }));
      setCustomerFormErrors(validation.errors);
      toast.error("Please fix form errors before adding customer");
      return;
    }

    dispatch(setLoading(true));
    try {
      const docRef = await addDoc(collection(db, "customers"), {
        ...payload,
        createdAt: Timestamp.now(),
      });
      // treat as selected after creation
      setIsCustomerSelected(true);
      toast.success(`Customer added`);
      // advance to next step
      setCurrentStep(2);
    } catch (err) {
      toast.error(err?.message || "Failed to add customer");
    } finally {
      dispatch(setLoading(false));
    }
  };



  const [ornaments, setOrnaments] = useState([
    { name: "", units: "", net: "" },
  ]);
  const [ornamentImage, setOrnamentImage] = useState(null);

  // derived computed values
  const goldRate = Number(form.goldRate) || 0;

  useEffect(() => {
    const w = parseGramInput(form.totalWeight);
    const rate = Number(form.goldRate) || 0;
    const amount = w > 0 && rate > 0 ? calculateOrnamentAmount(w, rate) : 0;
    const nextLoanStr = amount > 0 ? String(amount) : "";
    setForm((prev) => {
      if (prev.loanAmount === nextLoanStr) return prev;
      return { ...prev, loanAmount: nextLoanStr };
    });
  }, [form.totalWeight, form.goldRate]);

  useEffect(() => {
    setIsSaved(false);
  }, [form, ornaments, goldRate]);

  const ornamentRowsWithAmount = useMemo(
    () =>
      ornaments.map((r) => {
        const units = Number(r.units) || 1;
        const netGrams = parseGramInput(r.net);
        const netAmount = calculateOrnamentAmount(netGrams, goldRate) * units;
        return {
          ...r,
          netAmount,
          amount: netAmount, // legacy field used elsewhere
        };
      }),
    [ornaments, goldRate]
  );

  const ornamentTotal = useMemo(
    () => ornamentRowsWithAmount.reduce((sum, row) => sum + (Number(row.netAmount) || 0), 0),
    [ornamentRowsWithAmount]
  );

  const totalOrnamentNetGrams = useMemo(
    () => calculateTotalOrnamentNetGrams(ornamentRowsWithAmount),
    [ornamentRowsWithAmount]
  );

  const ornamentCostFromTotalNet = useMemo(
    () => calculateOrnamentAmount(totalOrnamentNetGrams, goldRate),
    [totalOrnamentNetGrams, goldRate]
  );

  const interestAmount = useMemo(
    () => calculateInterest(form.loanAmount, form.interest, form.tenure),
    [form.loanAmount, form.interest, form.tenure]
  );

  const grandTotalPayable = useMemo(
    () => calculateTotalPayable(form.loanAmount, interestAmount),
    [form.loanAmount, interestAmount]
  );

  const processingFeeAmount = useMemo(() => {
    const loan = Number(form.loanAmount) || 0;
    const feePercent = Number(form.processingFeePercent) || 0;
    return (loan * feePercent) / 100;
  }, [form.loanAmount, form.processingFeePercent]);

  const gstAmount = useMemo(() => {
    if (!form.includeGst) return 0;
    const loan = Number(form.loanAmount) || 0;
    const gstPct = Number(form.gstPercent) || 0;
    return (loan * gstPct) / 100;
  }, [form.includeGst, form.loanAmount, form.gstPercent]);

  const netLoanAmount = useMemo(() => {
    const loan = Number(form.loanAmount) || 0;
    return loan - processingFeeAmount - gstAmount;
  }, [form.loanAmount, processingFeeAmount, gstAmount]);

  // validate on change (live validation)
  useEffect(() => {
    const { errors: vErrors } = validateBillingForm({
      ...form,
      goldRate,
      ornaments,
    });

    if (form.includeGst && !form.gstPercent) {
      vErrors.gstPercent = "GST % is required";
    }

    const filteredErrors = Object.entries(vErrors).reduce((acc, [key, message]) => {
      const isOrnamentKey = key.includes("_");
      if (isOrnamentKey) {
        if (touchedOrnaments[key]) acc[key] = message;
      } else if (touchedFields[key]) {
        acc[key] = message;
      }
      return acc;
    }, {});

    setErrors(filteredErrors);
  }, [form, ornaments, goldRate, touchedFields, touchedOrnaments]);

  /* ---------- Validation ---------- */

  const validateField = (key, value) => {
    setTouchedFields((prev) => ({ ...prev, [key]: true }));
  };

  const validateOrnamentField = (i, key, value) => {
    setTouchedOrnaments((prev) => ({ ...prev, [`${key}_${i}`]: true }));
  };

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    validateField(k, v);
    // if user edits any customer-related field after selection, mark as not-selected
    const customerKeys = [
      "customerId","customerName","phone","email","address",
      "bankName","branch","accountNumber","ifsc",
      "profilePicture","profilePictureName","aadharCard","aadharCardName",
      "panCard","panCardName"
    ];
    if (customerKeys.includes(k)) setIsCustomerSelected(false);
  };

  const updateRow = (i, k, v) => {
    const cp = [...ornaments];
    cp[i][k] = v;
    setOrnaments(cp);
    validateOrnamentField(i, k, v);
  };

  const setGoldRate = (val) => {
    setForm((prev) => ({ ...prev, goldRate: val }));
    validateField("goldRate", val);
  };

  /* ---------- Fetch API ---------- */
  const fetchLiveGoldRate = async () => {
    const tId = toast.loading("Checking live gold rates...");
    try {
      const response = await fetch("https://www.goldapi.io/api/XAU/INR", {
        headers: {
          "x-access-token": process.env.REACT_APP_GOLD_API_KEY || "goldapi-80rqlsmo6twlcw-io",
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok && data && data.price_gram_24k) {
        const rate = data.price_gram_22k || data.price_gram_24k;
        setGoldRate(Math.round(rate).toString());
        toast.success(`Live rate updated! ₹${Math.round(rate)}/g`);
      } else {
        throw new Error("Invalid API Key");
      }
    } catch (e) {
      toast.error("Real API requires a key in Production. Using fallback.", { id: tId });
      setGoldRate("6800");
    }
  };

  /* ---------- Date ---------- */

  const calcTenure = (s, e) => {
    const t = calculateTenure(s, e);
    setField("tenure", t || "");
  };

  const handleDate = (type, val) => {
    if (type === "start") {
      setField("startDate", val);
      calcTenure(val, form.endDate);
    } else {
      setField("endDate", val);
      calcTenure(form.startDate, val);
    }
  };

  /* ---------- Image Upload ---------- */

  const handleGlobalImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ORNAMENT_FILE_BYTES) {
      toast.error("File too large. Max 2 MB allowed.");
      try { event.target.value = ""; } catch (e) { }
      return;
    }

    try {
      const base64 = await compressImageFile(file);
      setOrnamentImage(base64);
    } catch (err) {
      toast.error("Failed to read image");
    }
  };

  const addRow = () =>
    setOrnaments((o) => [
      ...o,
      { name: "", units: "", net: "" },
    ]);

  const deleteRow = (index) => {
    if (ornaments.length <= 1) return;
    const cp = [...ornaments];
    cp.splice(index, 1);
    setOrnaments(cp);
    setTouchedOrnaments((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const match = key.match(/^([a-zA-Z]+)_(\d+)$/);
        if (!match) return;
        const field = match[1];
        const i = Number(match[2]);
        if (i < index) next[key] = value;
        else if (i > index) next[`${field}_${i - 1}`] = value;
      });
      return next;
    });
  };

  /* ---------- SAVE TO FIREBASE ---------- */

  const saveDataToFirestore = async () => {
    const {
      aadharCard: _aadhar,
      aadharCardName: _aadharName,
      panCard: _pan,
      panCardName: _panName,
      ...loanForm
    } = form;

    const profilePicture = loanForm.profilePicture
      ? await compressDataUrl(loanForm.profilePicture, {
          maxWidth: 320,
          maxHeight: 320,
          maxBytes: 90000,
        })
      : loanForm.profilePicture;

    const savedOrnamentImage = ornamentImage
      ? await compressDataUrl(ornamentImage, {
          maxWidth: 1600,
          maxHeight: 1600,
          maxBytes: 450000,
        })
      : ornamentImage;

    const data = {
      customerId,
      form: { ...loanForm, profilePicture },
      ornamentImage: savedOrnamentImage,
      ornaments: ornamentRowsWithAmount,
      createdAt: Timestamp.now(),
    };
    await addDoc(collection(db, "billing"), data);
  };

  const handleSave = async () => {
    const declaredNet = parseGramInput(form.totalWeight);
    const ornamentsNet = totalOrnamentNetGrams;
    if (Number(declaredNet.toFixed(3)) !== Number(ornamentsNet.toFixed(3))) {
      toast.error(
        `Total net weight in ornaments (${Number(ornamentsNet.toFixed(3))} g) must match Loan Total Weight (${Number(declaredNet.toFixed(3))} g).`
      );
      return;
    }

    dispatch(setLoading(true));
    try {
      await saveDataToFirestore();
      setIsSaved(true);
      toast.success("Data saved successfully ✅");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      dispatch(setLoading(false));
    }
  };

  /* ---------- PRINT ---------- */

  const handlePrint = async () => {
    if (!isSaved) {
      toast.error("Please save first");
      return;
    }

    dispatch(setLoading(true));
    try {
      // Prepare PDF payload
      const payload = {
        billingId: customerId,
        branchName: form.bankName,
        customer: form,
        loan: {
          ...form,
          interestAmount,
          totalPayable: grandTotalPayable,
          processingFeeAmount,
          gstAmount,
          netLoanAmount,
        },
        goldRate,
        ornamentImage,
        ornaments: ornamentRowsWithAmount.map((r) => ({
          description: r.name,
          units: r.units,
          gross: r.gross,
          net: r.net,
          amount: r.amount,
        })),
        totals: {
          ornamentTotal,
          totalPayable: grandTotalPayable,
          totalOrnamentNetGrams,
          ornamentCostFromTotalNet,
        },
      };

      generateBillingInvoicePdf(payload);
      toast.success("Done ✅");
    } catch (err) {
      toast.error("Something went wrong during print process");
    } finally {
      dispatch(setLoading(false));
    }
  };

  /* ---------- INPUT HANDLER WRAPPER ---------- */

  const handleChange = (key, value) => {
    let newValue = value;

    // numeric fields
    if (["phone", "accountNumber"].includes(key)) {
      newValue = onlyNumbers(value);

      // limit phone to 10 digits
      if (key === "phone") {
        newValue = newValue.slice(0, 10);
      }
    }

    if (key === "totalWeight") {
      newValue = value.replace(/[^\d.]/g, "");
    }

    // text fields with capitalization
    if (["bankName", "customerName"].includes(key)) {
      newValue = onlyText(value);
    }

    setField(key, newValue);
  };

  const handleOrnamentNetChange = (i, val) => {
    // allow numbers only, but keep decimals
    const cleaned = val.replace(/[^\d.]/g, "");
    updateRow(i, "net", cleaned);
  };

  const handleOrnamentGrossChange = (i, val) => {
    const cleaned = val.replace(/[^\d.]/g, "");
    updateRow(i, "gross", cleaned);
  };

  const isFormValid = useMemo(() => {
    const validation = validateBillingForm({
      ...form,
      goldRate,
      ornaments,
    });

    let isValid = validation.isValid;
    if (form.includeGst && !form.gstPercent) {
      isValid = false;
    }

    // validateBillingForm does not check for empty fields in ornaments, so we check explicitly
    const areOrnamentsFilled = ornaments.every(
      (o) => o.name && String(o.name).trim() !== "" &&
        o.units !== "" && o.units != null &&
        o.net !== "" && o.net != null
    );

    return isValid && areOrnamentsFilled;
  }, [form, goldRate, ornaments]);

  const isStep2Valid = useMemo(() => {
    const wOk = parseGramInput(form.totalWeight) > 0;
    const rateOk = Number(form.goldRate) > 0;
    let valid =
      form.startDate &&
      form.endDate &&
      wOk &&
      rateOk &&
      form.loanAmount &&
      form.processingFeePercent &&
      form.interest;
    if (form.includeGst && !form.gstPercent) valid = false;
    return Boolean(valid);
  }, [
    form.startDate,
    form.endDate,
    form.totalWeight,
    form.goldRate,
    form.loanAmount,
    form.processingFeePercent,
    form.interest,
    form.includeGst,
    form.gstPercent,
  ]);

  /* ---------- UI ---------- */

  const filteredCustomersList = customersList.filter(cust => {
    const query = customerSearchQuery.toLowerCase();
    const nameMatch = cust.customerName?.toLowerCase().includes(query);
    const idMatch = cust.customerId?.toLowerCase().includes(query);
    return nameMatch || idMatch;
  });

  return (
    <Container>
      
      <Card>
        <StepperContainer>
          <StepperHeader>
            <StepText>Step {currentStep} of 3</StepText>
            <StepText style={{ color: '#6b7280', fontSize: '13px', fontWeight: '500' }}>
              {currentStep === 1 && "Customer Details"}
              {currentStep === 2 && "Loan Information"}
              {currentStep === 3 && "Ornaments & Summary"}
            </StepText>
          </StepperHeader>
          <ProgressBarWrapper>
            <ProgressBarFill progress={(currentStep / 3) * 100} />
          </ProgressBarWrapper>
        </StepperContainer>

        <PrintArea ref={printRef}>
          <InvoiceHeader>
            <Title>Gold Loan Billing</Title>

            <HeaderRight>
              <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#111827" }}>Customer ID: {form.customerId || "Customer ID"}</h2>
              <div style={{ color: "#6b7280", fontSize: "0.9rem", marginTop: "4px" }}>Billing ID: {customerId}</div>
            </HeaderRight>
          </InvoiceHeader>

          <StepWrapper isActive={currentStep === 1}>
            <SectionHeader>
              <SectionTitle style={{ margin: 0 }}>Customer Details</SectionTitle>
              <HeaderButtons>
                <OutlinedButton
                  type="button"
                  style={{ margin: 0, padding: "8px 16px", fontSize: "14px" }}
                  onClick={fetchCustomers}
                  disabled={isFetchingCustomers}
                >
                  Choose Customer
                </OutlinedButton>
              </HeaderButtons>
            </SectionHeader>

            <CreateFormGrid>
              <CreateFormRow>
                <label>Customer ID</label>
                <input
                  value={form.customerId}
                  onChange={(e) => setField("customerId", e.target.value)}
                />
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.customerName}>
                <label>Customer Name</label>
                <input
                  name="customerName"
                  value={form.customerName}
                  onChange={(e) => setField("customerName", e.target.value)}
                />
                {customerFormErrors.customerName && <CreateErrorText>{customerFormErrors.customerName}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.phone}>
                <label>Phone Number</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setField("phone", onlyNumbers(e.target.value).slice(0, 10))}
                />
                {customerFormErrors.phone && <CreateErrorText>{customerFormErrors.phone}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.email}>
                <label>Email</label>
                <input
                  name="email"
                  value={form.email || ""}
                  onChange={(e) => setField("email", e.target.value)}
                />
                {customerFormErrors.email && <CreateErrorText>{customerFormErrors.email}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.address}>
                <label>Address</label>
                <input
                  name="address"
                  value={form.address || ""}
                  onChange={(e) => setField("address", e.target.value)}
                />
                {customerFormErrors.address && <CreateErrorText>{customerFormErrors.address}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.bankName}>
                <label>Bank Name</label>
                <input
                  name="bankName"
                  value={form.bankName}
                  onChange={(e) => setField("bankName", e.target.value)}
                />
                {customerFormErrors.bankName && <CreateErrorText>{customerFormErrors.bankName}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.branch}>
                <label>Branch</label>
                <input
                  name="branch"
                  value={form.branch || ""}
                  onChange={(e) => setField("branch", e.target.value)}
                />
                {customerFormErrors.branch && <CreateErrorText>{customerFormErrors.branch}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.accountNumber}>
                <label>Account Number</label>
                <input
                  name="accountNumber"
                  value={form.accountNumber}
                  onChange={(e) => setField("accountNumber", onlyNumbers(e.target.value))}
                />
                {customerFormErrors.accountNumber && <CreateErrorText>{customerFormErrors.accountNumber}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.ifsc}>
                <label>IFSC Code</label>
                <input
                  name="ifsc"
                  value={form.ifsc}
                  onChange={(e) => setField("ifsc", e.target.value.toUpperCase())}
                />
                {customerFormErrors.ifsc && <CreateErrorText>{customerFormErrors.ifsc}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.profilePicture}>
                <label>Profile Picture</label>
                <input
                  name="profilePicture"
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleCustomerFileUpload(event, "profilePicture", "profilePictureName")}
                />
                {form.profilePictureName && <small>{form.profilePictureName}</small>}
                {customerFormErrors.profilePicture && <CreateErrorText>{customerFormErrors.profilePicture}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.aadharCard}>
                <label>Aadhar Card</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => handleCustomerFileUpload(event, "aadharCard", "aadharCardName")}
                />
                {form.aadharCardName && <small>{form.aadharCardName}</small>}
                {customerFormErrors.aadharCard && <CreateErrorText>{customerFormErrors.aadharCard}</CreateErrorText>}
              </CreateFormRow>

              <CreateFormRow hasError={customerFormErrors.panCard}>
                <label>PAN Card</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => handleCustomerFileUpload(event, "panCard", "panCardName")}
                />
                {form.panCardName && <small>{form.panCardName}</small>}
                {customerFormErrors.panCard && <CreateErrorText>{customerFormErrors.panCard}</CreateErrorText>}
              </CreateFormRow>
            </CreateFormGrid>
            <StepNavigation>
              {isCustomerSelected ? (
                <Button onClick={() => setCurrentStep(2)}>Next</Button>
              ) : (
                <Button onClick={handleAddCustomer}>Add Customer</Button>
              )}
            </StepNavigation>
          </StepWrapper>

          <StepWrapper isActive={currentStep === 2}>
            <SectionTitle>Loan</SectionTitle>

            <Grid>
              <Field>
                <Label>Start Date</Label>
                <InputWrapper hasError={errors.startDate}>
                  <Input
                    type={form.startDate ? "date" : "text"}
                    placeholder="Start Date"
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.type = "text";
                    }}
                    onChange={(e) => handleDate("start", e.target.value)}
                  />
                </InputWrapper>
                {errors.startDate && <Error>{errors.startDate}</Error>}
              </Field>

              <Field>
                <Label>End Date</Label>
                <InputWrapper hasError={errors.endDate}>
                  <Input
                    type={form.endDate ? "date" : "text"}
                    placeholder="End Date"
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.type = "text";
                    }}
                    onChange={(e) => handleDate("end", e.target.value)}
                  />
                </InputWrapper>
                {errors.endDate && <Error>{errors.endDate}</Error>}
              </Field>

              <Field>
                <Label>Tenure</Label>
                <InputWrapper>
                  <Input value={form.tenure} placeholder="Loan Tenure" readOnly />
                  <Suffix>days</Suffix>
                </InputWrapper>
              </Field>

              <Field>
                <Label>Total Weight</Label>
                <InputWrapper>
                  <Input
                    value={form.totalWeight}
                    placeholder="e.g. 45.5"
                    onChange={(e) =>
                      handleChange("totalWeight", e.target.value)
                    }
                  />
                  <Suffix>g</Suffix>
                </InputWrapper>
              </Field>

              <Field>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <Label>Today&apos;s Gold Rate</Label>
                  <span
                    onClick={fetchLiveGoldRate}
                    style={{ fontSize: "11px", color: "#2563EB", cursor: "pointer", fontWeight: "600", marginBottom: "6px" }}
                    title="Fetch live rate from market"
                  >
                    Fetch Live
                  </span>
                </div>
                <InputWrapper hasError={errors.goldRate}>
                  <Prefix>Rs/g</Prefix>
                  <Input
                    value={form.goldRate}
                    placeholder="Rate"
                    onChange={(e) => setGoldRate(onlyNumbers(e.target.value))}
                  />
                </InputWrapper>
                {errors.goldRate && <Error>{errors.goldRate}</Error>}
              </Field>

              <Field>
                <Label>Loan Amount</Label>
                <InputWrapper hasError={errors.loanAmount} highlight={true} readOnly>
                  <Prefix>Rs</Prefix>
                  <Input
                    value={form.loanAmount ? formatCurrencyINR(Number(form.loanAmount)) : ""}
                    placeholder="Auto from weight × rate"
                    readOnly
                  />
                </InputWrapper>
                {errors.loanAmount && <Error>{errors.loanAmount}</Error>}
              </Field>

              <Field>
                <Label>Processing Fee %</Label>
                <InputWrapper hasError={errors.processingFeePercent}>
                  <Input
                    value={form.processingFeePercent}
                    placeholder="Processing Fee %"
                    onChange={(e) =>
                      handleChange("processingFeePercent", e.target.value)
                    }
                  />
                  <Suffix>%</Suffix>
                </InputWrapper>
                {errors.processingFeePercent && <Error>{errors.processingFeePercent}</Error>}
              </Field>

              <Field style={{ flexDirection: 'row', alignItems: 'center', gap: '10px', height: '42px', marginTop: '24px' }}>
                <input
                  type="checkbox"
                  checked={form.includeGst || false}
                  onChange={(e) => setField("includeGst", e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  id="includeGstCheckbox"
                />
                <label htmlFor="includeGstCheckbox" style={{ fontSize: '14px', cursor: 'pointer', margin: 0, fontWeight: 500, color: '#374151' }}>Include GST</label>
              </Field>

              {form.includeGst && (
                <Field>
                  <Label>GST %</Label>
                  <InputWrapper hasError={errors.gstPercent}>
                    <Input
                      value={form.gstPercent || ""}
                      placeholder="GST %"
                      onChange={(e) =>
                        handleChange("gstPercent", e.target.value)
                      }
                    />
                    <Suffix>%</Suffix>
                  </InputWrapper>
                  {errors.gstPercent && <Error>{errors.gstPercent}</Error>}
                </Field>
              )}

              <Field>
                <Label>Net Loan Amount</Label>
                <InputWrapper readOnly highlight={true}>
                  <Prefix>Rs</Prefix>
                  <Input
                    value={form.loanAmount && form.processingFeePercent ? formatCurrencyINR(netLoanAmount) : ""}
                    placeholder="Net Loan Amount"
                    readOnly
                  />
                </InputWrapper>
              </Field>

              <Field>
                <Label>Interest</Label>
                <InputWrapper hasError={errors.interest}>
                  <Input
                    value={form.interest}
                    placeholder="Interest %"
                    onChange={(e) =>
                      handleChange("interest", e.target.value)
                    }
                  />
                  <Suffix>%</Suffix>
                </InputWrapper>
                {errors.interest && <Error>{errors.interest}</Error>}
              </Field>

              <Field>
                <Label>Amount with Interest</Label>
                <InputWrapper readOnly={!(form.startDate && form.endDate && form.loanAmount && form.interest)}>
                  <Prefix>Rs</Prefix>
                  <Input
                    value={(form.startDate && form.endDate && form.loanAmount && form.interest) ? formatCurrencyINR(Number(form.loanAmount) + Number(interestAmount)) : ""}
                    placeholder="Amount with Interest"
                    readOnly
                  />
                </InputWrapper>
              </Field>
            </Grid>
            <StepNavigation>
              <OutlinedButton onClick={() => setCurrentStep(1)}>Back</OutlinedButton>
              <Button disabled={!isStep2Valid} onClick={() => setCurrentStep(3)}>Next</Button>
            </StepNavigation>
          </StepWrapper>

          <StepWrapper isActive={currentStep === 3}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap", margin: " 16px 0" }}>
              <SectionTitle style={{ margin: 0, whiteSpace: "nowrap" }}>Ornaments</SectionTitle>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <CustomUploadButton>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGlobalImageUpload}
                  />
                </CustomUploadButton>
                {ornamentImage && (
                  <img
                    src={ornamentImage}
                    alt="ornaments"
                    height="40"
                    style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                  />
                )}
              </div>
            </div>

            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Units</th>
                    <th>Gross (g)</th>
                    <th>Net (g)</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {ornamentRowsWithAmount.map((o, i) => (
                    <tr key={i}>
                      <td>
                        <InputWrapper hasError={errors[`name_${i}`]}>
                          <Input
                            value={o.name}
                            onChange={(e) =>
                              updateRow(i, "name", e.target.value)
                            }
                            placeholder="Ring"
                          />
                        </InputWrapper>
                        {errors[`name_${i}`] && <Error style={{ display: 'block' }}>{errors[`name_${i}`]}</Error>}
                      </td>

                      <td>
                        <InputWrapper hasError={errors[`units_${i}`]}>
                          <Input
                            value={o.units}
                            onChange={(e) =>
                              updateRow(i, "units", onlyNumbers(e.target.value))
                            }
                            placeholder="1"
                          />
                        </InputWrapper>
                        {errors[`units_${i}`] && <Error style={{ display: 'block' }}>{errors[`units_${i}`]}</Error>}
                      </td>

                      <td>
                        <InputWrapper hasError={errors[`gross_${i}`]}>
                          <Input
                            value={o.gross}
                            onChange={(e) =>
                              handleOrnamentGrossChange(i, e.target.value)
                            }
                            placeholder="10"
                          />
                          <Suffix>g</Suffix>
                        </InputWrapper>
                        {errors[`gross_${i}`] && <Error style={{ display: 'block' }}>{errors[`gross_${i}`]}</Error>}
                      </td>

                      <td>
                        <InputWrapper hasError={errors[`net_${i}`]}>
                          <Input
                            value={o.net}
                            onChange={(e) =>
                              handleOrnamentNetChange(i, e.target.value)
                            }
                            placeholder="10"
                          />
                          <Suffix>g</Suffix>
                        </InputWrapper>
                        {errors[`net_${i}`] && <Error style={{ display: 'block' }}>{errors[`net_${i}`]}</Error>}
                      </td>

                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <IconButton
                            onClick={() => deleteRow(i)}
                            disabled={ornaments.length <= 1}
                            title={ornaments.length <= 1 ? "Cannot delete last row" : "Delete row"}
                            aria-label="Delete row"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M10 11v6" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M14 11v6" stroke="#9ca3af" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </IconButton>
                          <IconButton
                            onClick={addRow}
                            title="Add row"
                            aria-label="Add row"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            <MobileContainer>
              {ornamentRowsWithAmount.map((o, i) => (
                <MobileCard key={i}>
                  <MobileRow>
                    <MobileLabel>Description</MobileLabel>
                    <InputWrapper hasError={errors[`name_${i}`]}>
                      <Input
                        value={o.name}
                        onChange={(e) =>
                          updateRow(i, "name", e.target.value)
                        }
                        placeholder="Ring"
                      />
                    </InputWrapper>
                    {errors[`name_${i}`] && <Error>{errors[`name_${i}`]}</Error>}
                  </MobileRow>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <MobileRow>
                      <MobileLabel>Units (g)</MobileLabel>
                      <InputWrapper hasError={errors[`units_${i}`]}>
                        <Input
                          value={o.units}
                          onChange={(e) =>
                            updateRow(i, "units", onlyNumbers(e.target.value))
                          }
                          placeholder="1"
                        />
                        <Suffix>g</Suffix>
                      </InputWrapper>
                      {errors[`units_${i}`] && <Error>{errors[`units_${i}`]}</Error>}
                    </MobileRow>

                    <MobileRow>
                      <MobileLabel>Net (g)</MobileLabel>
                      <InputWrapper hasError={errors[`net_${i}`]}>
                        <Input
                          value={o.net}
                          onChange={(e) =>
                            handleOrnamentNetChange(i, e.target.value)
                          }
                          placeholder="10"
                        />
                        <Suffix>g</Suffix>
                      </InputWrapper>
                      {errors[`net_${i}`] && <Error>{errors[`net_${i}`]}</Error>}
                    </MobileRow>
                  </div>

                  <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: '8px' }}>
                    <DeleteBtnMobile
                      onClick={() => deleteRow(i)}
                      disabled={ornaments.length <= 1}
                      title={ornaments.length <= 1 ? "Cannot delete last row" : "Delete row"}
                      style={{ position: 'static' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 11v6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </DeleteBtnMobile>
                    <DeleteBtnMobile
                      onClick={addRow}
                      title="Add row"
                      style={{ position: 'static', background: '#DBEAFE', color: '#1E40AF' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </DeleteBtnMobile>
                  </div>
                </MobileCard>
              ))}
            </MobileContainer>

            <Step3SummaryBand>
              <OrnamentTotalsBox>
                <SummaryRow>
                  <span>Total Net (g): </span>
                  <strong>
                    {Number.isFinite(totalOrnamentNetGrams)
                      ? `${Number(totalOrnamentNetGrams.toFixed(3)).toString()} g`
                      : "0 g"}
                  </strong>
                </SummaryRow>
                <SummaryRow>
                  <span>Ornament cost: </span>
                  <strong>{formatCurrencyINR(ornamentCostFromTotalNet)}</strong>
                </SummaryRow>
              </OrnamentTotalsBox>
              <LoanSummaryBoxSide>
                <SummaryRow>
                  <span>Loan amount: </span>
                  <strong>{formatCurrencyINR(form.loanAmount)}</strong>
                </SummaryRow>
                <SummaryRow>
                  <span>Processing fee: </span>
                  <strong>{formatCurrencyINR(processingFeeAmount)}</strong>
                </SummaryRow>
                {form.includeGst && (
                  <SummaryRow>
                    <span>GST: </span>
                    <strong>{formatCurrencyINR(gstAmount)}</strong>
                  </SummaryRow>
                )}
                <SummaryTotalRow>
                  <span>Net loan amount: </span>
                  <strong style={{ color: "#2563eb", fontSize: "18px" }}>{formatCurrencyINR(netLoanAmount)}</strong>
                </SummaryTotalRow>
              </LoanSummaryBoxSide>
            </Step3SummaryBand>

            <StepNavigation>
              <OutlinedButton onClick={() => setCurrentStep(2)}>Back</OutlinedButton>
              <div style={{ display: "flex", gap: "10px" }}>
                <OutlinedButton onClick={handlePrint} disabled={!isFormValid}>Generate PDF</OutlinedButton>
                <Button onClick={handleSave} disabled={!isFormValid}>Save Loan</Button>
              </div>
            </StepNavigation>
          </StepWrapper>
        </PrintArea>
      </Card>
      
 

      {/* CHOOSE CUSTOMER MODAL */}
      {showCustomerModal && (
        <ModalOverlay onClick={() => setShowCustomerModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Select Customer</h3>
              <CloseButton onClick={() => setShowCustomerModal(false)}>&times;</CloseButton>
            </div>
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={customerSearchQuery}
                onChange={(e) => setCustomerSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #D1D5DB",
                  fontSize: "14px",
                  boxSizing: "border-box"
                }}
              />
            </div>
            {filteredCustomersList.length === 0 ? (
              <p>No customers found.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {filteredCustomersList.map((cust) => (
                  <CustomerCardItem key={cust.id} onClick={() => handleSelectCustomer(cust)}>
                    <div style={{ fontWeight: 600, color: "#111827" }}>
                      {cust.customerName || "Unnamed"} {cust.customerId ? `- ${cust.customerId}` : ""}
                    </div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>Phone: {cust.phone || "-"}</div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>Bank: {cust.bankName || "-"} / {cust.accountNumber || "-"}</div>
                  </CustomerCardItem>
                ))}
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Billing;